import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './ProjectThread.css';
import lpApi from '../../helpers/lpApi';

// =============================================================================
// ProjectThread — shared in-project MESSAGING view for the Custom Projects
// (Gros Projets) module, used by BOTH the client (MyProjects) and the company
// (CompanyProjects) surfaces. Step #3 of the client -> company -> accept loop.
//
// All backend ops live on besthand-api, reached through the `/lp-graphql`
// proxy via the `lpApi` helper (which forwards the logged-in id_token cookie).
//
// COMPLIANCE / Anti-circumvention: the server runs filterCircumventionContent
// on every send and persists/returns the FILTERED text ([REDACTED]). We render
// exactly what the server returns and NEVER re-filter or reconstruct contact
// info client-side. A persistent safety note tells users that phone numbers,
// emails and links are removed automatically.
//
// MASKING: the counterparty label is supplied by the parent as `counterparty`.
//   - Client side: masked company ref until selection ("Selected company" /
//     "Company #N" once revealed).
//   - Company side: the anonymous client ref (anonymousClientRef).
// This component never derives identity itself — it only renders the label.
//
// PARTICIPATION: messaging requires (projectRequestId, companyId) and is only
// available once the company has SUBMITTED a quote (enforced server-side in
// sendProjectMessage / projectMessageThread). The parent only mounts this on a
// project that satisfies that gate.
//
// SSR-safe: no window/document access at module load or render; effects run in
// lifecycle (componentDidMount) and click/submit handlers only.
// =============================================================================

// ---- GraphQL operations (against besthand-api ProjectsResponseType) ----

// Resolve the thread for this (project, company) pair from the caller's
// threads list, then load its messages. We resolve via myProjectMessageThreads
// (no threadId needed up front) and, when found, hydrate via projectMessageThread.
const Q_MY_THREADS = `
  query {
    myProjectMessageThreads {
      status errorMessage
      messageThreads {
        id projectRequestId companyId clientUserId status
        lastMessageAt unreadCountClient unreadCountCompany
      }
    }
  }
`;

const Q_THREAD = `
  query ($threadId: String!) {
    projectMessageThread(threadId: $threadId) {
      status errorMessage
      messageThread {
        id projectRequestId companyId clientUserId status
        messages {
          id senderUserId senderRole text hadCircumventionAttempt createdAt
        }
      }
    }
  }
`;

const M_SEND = `
  mutation ($projectRequestId: String!, $companyId: String!, $text: String!) {
    sendProjectMessage(projectRequestId: $projectRequestId, companyId: $companyId, text: $text) {
      status errorMessage
      messageWasFiltered messageFilteredPatterns
      message {
        id senderUserId senderRole text hadCircumventionAttempt createdAt
      }
    }
  }
`;

// ---- inline icons (reused from the Custom Projects surfaces — none invented) ----
const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ICONS = {
  shield: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z',
  send: 'M22 2 11 13 M22 2l-7 20-4-9-9-4 20-7Z',
  back: 'M15 18l-6-6 6-6',
};

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const MAX_LEN = 4000;

class ProjectThread extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: '',
      threadId: null,
      messages: [],
      draft: '',
      sending: false,
      sendError: '',
      lastFiltered: false, // true when the most recent send was redacted
    };
    this.listRef = React.createRef();
  }

  componentDidMount() {
    this.loadThread();
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong.';
  }

  // Find the (project, company) thread among the caller's threads, then hydrate
  // its messages. A missing thread is a valid empty state (no messages yet).
  async loadThread() {
    const { projectRequestId, companyId } = this.props;
    this.setState({ loading: true, error: '' });
    try {
      const res = await lpApi(Q_MY_THREADS);
      const env = res && res.data && res.data.myProjectMessageThreads;
      if (res.errors || !env || (env.status && env.status >= 400)) {
        this.setState({ loading: false, error: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      const threads = env.messageThreads || [];
      const match = threads.find(
        (t) => String(t.projectRequestId) === String(projectRequestId)
          && String(t.companyId) === String(companyId),
      );
      if (!match) {
        // No thread yet — first message will create it.
        this.setState({ loading: false, threadId: null, messages: [] });
        return;
      }
      await this.hydrateMessages(match.id);
    } catch (e) {
      this.setState({ loading: false, error: `Could not load messages: ${e.message}` });
    }
  }

  async hydrateMessages(threadId) {
    const res = await lpApi(Q_THREAD, { threadId: String(threadId) });
    const env = res && res.data && res.data.projectMessageThread;
    if (res.errors || !env || (env.status && env.status >= 400)) {
      this.setState({ loading: false, error: (env && env.errorMessage) || this.firstError(res) });
      return;
    }
    const thread = env.messageThread || {};
    this.setState(
      { loading: false, threadId, messages: thread.messages || [] },
      () => this.scrollToBottom(),
    );
  }

  scrollToBottom() {
    const el = this.listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  async sendMessage() {
    const { projectRequestId, companyId } = this.props;
    const text = (this.state.draft || '').trim();
    if (!text || this.state.sending) return;
    this.setState({ sending: true, sendError: '' });
    try {
      const res = await lpApi(M_SEND, {
        projectRequestId: String(projectRequestId),
        companyId: String(companyId),
        text,
      });
      const env = res && res.data && res.data.sendProjectMessage;
      if (res.errors || !env || (env.status && env.status >= 400)) {
        this.setState({ sending: false, sendError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      // Append the SERVER-returned (already filtered) message. We render its
      // `text` verbatim — never re-filter or reconstruct on the client.
      const saved = env.message;
      this.setState(
        (st) => ({
          sending: false,
          draft: '',
          lastFiltered: !!env.messageWasFiltered,
          threadId: st.threadId || (saved ? saved.threadId : st.threadId),
          messages: saved ? [...st.messages, saved] : st.messages,
        }),
        () => {
          this.scrollToBottom();
          // If this was the first message, the thread now exists — resolve its
          // id for subsequent loads (best-effort, non-blocking).
          if (!this.state.threadId) this.loadThread();
        },
      );
    } catch (e) {
      this.setState({ sending: false, sendError: `Could not send: ${e.message}` });
    }
  }

  onKeyDown(e) {
    // Enter sends, Shift+Enter newlines.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  render() {
    const { counterparty, projectTitle, onBack } = this.props;
    const { loading, error, messages, draft, sending, sendError, lastFiltered } = this.state;
    const meIsCompany = this.props.viewerRole === 'COMPANY';

    return (
      <div className={s.thread}>
        <div className={s.head}>
          {onBack && (
            <button type="button" className={s.backBtn} onClick={onBack}>
              <Icon d={ICONS.back} /> Back
            </button>
          )}
          <div className={s.headMain}>
            <span className={s.headAvatar}><Icon d={ICONS.shield} /></span>
            <div>
              <span className={s.headName}>{counterparty || 'Conversation'}</span>
              {projectTitle ? <span className={s.headProject}>{projectTitle}</span> : null}
            </div>
          </div>
        </div>

        <div className={s.safetyNote}>
          <Icon d={ICONS.shield} />
          <span>
            For your safety, phone numbers, emails and links are automatically removed —
            keep everything on BestHand.
          </span>
        </div>

        {error ? <div className={s.alertErr}>{error}</div> : null}

        <div className={s.list} ref={this.listRef}>
          {loading ? (
            <div className={s.loading}>Loading messages…</div>
          ) : messages.length === 0 ? (
            <div className={s.emptyMsg}>
              <p>No messages yet.</p>
              <p className={s.emptyMsgSub}>
                {meIsCompany
                  ? 'Ask the homeowner anything you need to refine your quote — all on-platform.'
                  : 'Have a question for this company? Start the conversation right here.'}
              </p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = meIsCompany ? m.senderRole === 'COMPANY' : m.senderRole === 'CLIENT';
              return (
                <div key={m.id} className={`${s.row} ${mine ? s.rowMine : s.rowTheirs}`}>
                  <div className={`${s.bubble} ${mine ? s.bubbleMine : s.bubbleTheirs}`}>
                    <span className={s.bubbleText}>{m.text}</span>
                    <span className={s.bubbleTime}>{fmtTime(m.createdAt)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {lastFiltered && (
          <div className={s.filteredNote}>
            Some contact details in your last message were removed to keep this conversation
            on BestHand.
          </div>
        )}
        {sendError ? <div className={s.alertErr}>{sendError}</div> : null}

        <div className={s.composer}>
          <textarea
            className={s.composerInput}
            value={draft}
            maxLength={MAX_LEN}
            rows={2}
            placeholder="Write a message…"
            onChange={(e) => this.setState({ draft: e.target.value, sendError: '' })}
            onKeyDown={(e) => this.onKeyDown(e)}
          />
          <button
            type="button"
            className={s.sendBtn}
            disabled={sending || !(draft || '').trim()}
            onClick={() => this.sendMessage()}
            aria-label="Send message"
          >
            <Icon d={ICONS.send} />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(ProjectThread);
